import React, { useState } from 'react';
import { collection, getDocs, writeBatch, doc, Timestamp } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import Swal from 'sweetalert2';

const EliminarPropiedadesDuplicadas: React.FC = () => {
  const [ejecutando, setEjecutando] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);
  const [stats, setStats] = useState({
    totalPropiedades: 0,
    propiedadesObjetivo: 0,
    eliminadas: 0,
    errores: 0
  });

  const addLog = (message: string) => {
    setLogs(prev => [...prev, `[${new Date().toLocaleTimeString()}] ${message}`]);
    console.log(message);
  };

  const analizarPropiedadesDuplicadas = async () => {
    setLogs([]);
    addLog('🔍 Analizando propiedades duplicadas...');
    addLog('');

    try {
      const propiedadesRef = collection(db, 'propiedades');
      const snapshot = await getDocs(propiedadesRef);

      addLog(`📊 Total propiedades en BD: ${snapshot.size}`);
      addLog('');

      // Filtrar propiedades del 20/10/2025
      const propiedadesDel20 = snapshot.docs.filter(docSnap => {
        const data = docSnap.data();
        if (data.fecha_creacion) {
          const fecha = new Date(data.fecha_creacion.toDate());
          const fechaStr = fecha.toLocaleDateString('es-MX');
          return fechaStr === '20/10/2025';
        }
        return false;
      });

      const propiedadesDel21 = snapshot.docs.filter(docSnap => {
        const data = docSnap.data();
        if (data.fecha_creacion) {
          const fecha = new Date(data.fecha_creacion.toDate());
          const fechaStr = fecha.toLocaleDateString('es-MX');
          return fechaStr === '21/10/2025';
        }
        return false;
      });

      addLog(`📅 Propiedades del 20/10/2025: ${propiedadesDel20.length}`);
      addLog(`📅 Propiedades del 21/10/2025: ${propiedadesDel21.length}`);
      addLog('');

      setStats({
        totalPropiedades: snapshot.size,
        propiedadesObjetivo: propiedadesDel20.length,
        eliminadas: 0,
        errores: 0
      });

      if (propiedadesDel20.length === 0) {
        addLog('✅ No hay propiedades del 20/10/2025 para eliminar');
        return;
      }

      addLog('📋 MUESTRA DE PROPIEDADES A ELIMINAR (primeras 10):');
      addLog('');

      propiedadesDel20.slice(0, 10).forEach((docSnap, idx) => {
        const data = docSnap.data();
        addLog(`  ${idx + 1}. ${data.titulo || 'Sin título'} (List: ${data.list_number || 'N/A'})`);
        addLog(`     ID: ${docSnap.id}`);
        addLog(`     Área: ${data.direccion?.area || 'N/A'}`);
        addLog(`     Tipo: ${data.tipo_propiedad || 'N/A'}`);
        addLog('');
      });

      addLog('⚠️ IMPORTANTE:');
      addLog('  • Se eliminarán propiedades del 20/10/2025 (versión antigua)');
      addLog('  • Se conservarán propiedades del 21/10/2025 (versión correcta)');
      addLog(`  • Total a eliminar: ${propiedadesDel20.length} propiedades`);
      addLog(`  • Total a conservar: ${propiedadesDel21.length} propiedades`);
      addLog('');
      addLog('✅ Análisis completado. Usa el botón "Eliminar Duplicados" cuando estés listo.');

    } catch (error: any) {
      addLog(`❌ Error al analizar: ${error.message}`);
    }
  };

  const eliminarPropiedadesDuplicadas = async () => {
    const resultado = await Swal.fire({
      title: '⚠️ Confirmar Eliminación',
      html: `
        <div style="text-align: left;">
          <p><strong>¿Estás seguro de eliminar las propiedades del 20/10/2025?</strong></p>
          <br>
          <p>Se eliminarán aproximadamente <strong>${stats.propiedadesObjetivo} propiedades</strong></p>
          <p>Se conservarán las <strong>propiedades del 21/10/2025</strong> (las correctas)</p>
          <br>
          <p style="color: red;"><strong>⚠️ ESTA ACCIÓN NO SE PUEDE DESHACER</strong></p>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar duplicados',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6'
    });

    if (!resultado.isConfirmed) {
      addLog('❌ Eliminación cancelada por el usuario');
      return;
    }

    setEjecutando(true);
    addLog('');
    addLog('🗑️ INICIANDO ELIMINACIÓN DE DUPLICADOS...');
    addLog('');

    try {
      const propiedadesRef = collection(db, 'propiedades');
      const snapshot = await getDocs(propiedadesRef);

      // Filtrar propiedades del 20/10/2025
      const propiedadesDel20 = snapshot.docs.filter(docSnap => {
        const data = docSnap.data();
        if (data.fecha_creacion) {
          const fecha = new Date(data.fecha_creacion.toDate());
          const fechaStr = fecha.toLocaleDateString('es-MX');
          return fechaStr === '20/10/2025';
        }
        return false;
      });

      addLog(`📊 Propiedades a eliminar: ${propiedadesDel20.length}`);
      addLog('');

      let batch = writeBatch(db);
      let operacionesEnBatch = 0;
      let eliminadas = 0;
      let errores = 0;

      for (const docSnap of propiedadesDel20) {
        try {
          const propRef = doc(db, 'propiedades', docSnap.id);
          batch.delete(propRef);
          operacionesEnBatch++;
          eliminadas++;

          // Commit cada 500 operaciones
          if (operacionesEnBatch >= 500) {
            await batch.commit();
            addLog(`  ✅ Batch completado: ${eliminadas} propiedades eliminadas`);
            batch = writeBatch(db);
            operacionesEnBatch = 0;
          }

          setStats(prev => ({
            ...prev,
            eliminadas,
            errores
          }));

        } catch (error: any) {
          addLog(`  ❌ Error al eliminar ${docSnap.id}: ${error.message}`);
          errores++;
        }
      }

      // Commit final
      if (operacionesEnBatch > 0) {
        await batch.commit();
        addLog(`  ✅ Batch final completado`);
      }

      addLog('');
      addLog('✅ ELIMINACIÓN COMPLETADA');
      addLog('');
      addLog('📊 RESUMEN FINAL:');
      addLog(`  • Total eliminadas: ${eliminadas}`);
      addLog(`  • Errores: ${errores}`);
      addLog(`  • Propiedades restantes: ${snapshot.size - eliminadas}`);
      addLog('');

      // Verificar propiedades restantes
      const snapshotFinal = await getDocs(propiedadesRef);
      addLog(`✅ Verificación: ${snapshotFinal.size} propiedades en la base de datos`);

      await Swal.fire({
        icon: 'success',
        title: '✅ Eliminación Completada',
        html: `
          <div style="text-align: left;">
            <p><strong>Propiedades eliminadas:</strong> ${eliminadas}</p>
            <p><strong>Errores:</strong> ${errores}</p>
            <p><strong>Propiedades restantes:</strong> ${snapshotFinal.size}</p>
            <br>
            <p>Ahora tienes solo las propiedades correctas del 21/10/2025</p>
          </div>
        `
      });

    } catch (error: any) {
      addLog(`❌ Error durante la eliminación: ${error.message}`);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message
      });
    } finally {
      setEjecutando(false);
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1>🗑️ Eliminar Propiedades Duplicadas</h1>

      <div style={{
        backgroundColor: '#fff3cd',
        border: '1px solid #ffc107',
        borderRadius: '4px',
        padding: '15px',
        marginBottom: '20px'
      }}>
        <h3 style={{ color: '#856404', marginTop: 0 }}>⚠️ Atención</h3>
        <p style={{ margin: 0 }}>
          Este componente eliminará las propiedades duplicadas del <strong>20/10/2025</strong> (versión antigua con datos incorrectos)
          y conservará las propiedades del <strong>21/10/2025</strong> (versión correcta).
        </p>
      </div>

      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
        <button
          onClick={analizarPropiedadesDuplicadas}
          disabled={ejecutando}
          style={{
            padding: '10px 20px',
            fontSize: '16px',
            backgroundColor: ejecutando ? '#ccc' : '#17a2b8',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: ejecutando ? 'not-allowed' : 'pointer'
          }}
        >
          🔍 Analizar Duplicados
        </button>

        <button
          onClick={eliminarPropiedadesDuplicadas}
          disabled={ejecutando || stats.propiedadesObjetivo === 0}
          style={{
            padding: '10px 20px',
            fontSize: '16px',
            backgroundColor: ejecutando || stats.propiedadesObjetivo === 0 ? '#ccc' : '#dc3545',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: ejecutando || stats.propiedadesObjetivo === 0 ? 'not-allowed' : 'pointer'
          }}
        >
          {ejecutando ? '⏳ Eliminando...' : '🗑️ Eliminar Duplicados'}
        </button>
      </div>

      {/* Estadísticas */}
      {stats.totalPropiedades > 0 && (
        <div style={{
          backgroundColor: '#f8f9fa',
          padding: '15px',
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          <h3>📊 Estadísticas</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
            <div>
              <strong>Total propiedades:</strong> {stats.totalPropiedades}
            </div>
            <div style={{ color: '#dc3545' }}>
              <strong>A eliminar (20/10):</strong> {stats.propiedadesObjetivo}
            </div>
            <div style={{ color: '#28a745' }}>
              <strong>Eliminadas:</strong> {stats.eliminadas}
            </div>
            <div style={{ color: '#ffc107' }}>
              <strong>Errores:</strong> {stats.errores}
            </div>
          </div>
        </div>
      )}

      {/* Log */}
      <div style={{
        backgroundColor: '#f5f5f5',
        padding: '15px',
        borderRadius: '4px',
        maxHeight: '500px',
        overflow: 'auto'
      }}>
        <h3>📋 Log de Operaciones</h3>
        <div style={{
          fontFamily: 'monospace',
          fontSize: '12px',
          whiteSpace: 'pre-wrap'
        }}>
          {logs.map((log, index) => (
            <div key={index}>{log}</div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default EliminarPropiedadesDuplicadas;
